import { useEffect, useState, type FormEvent, type ChangeEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  createEvent,
  deleteEvent,
  fetchEvent,
  generateEventSlug,
  resolveEventImageUrl,
  updateEvent,
  uploadEventImage,
  type EventInput,
} from "@/lib/events";
import { handleApiError } from "@/lib/errors";
import { toast } from "@/components/ui/sonner";
import AdminEventSignupsList from "@/components/admin/AdminEventSignupsList";

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local: string): string {
  return new Date(local).toISOString();
}

interface FormState {
  title: string;
  slug: string;
  startsAtLocal: string;
  durationMinutes: string;
  location: string;
  body: string;
  imagePath: string;
  signupHref: string;
  isPublished: boolean;
  sortOrder: string;
  enableSignup: boolean;
  maxAttendees: string;
  signupDeadlineLocal: string;
}

const EMPTY_FORM: FormState = {
  title: "",
  slug: "",
  startsAtLocal: "",
  durationMinutes: "",
  location: "",
  body: "",
  imagePath: "",
  signupHref: "",
  isPublished: false,
  sortOrder: "0",
  enableSignup: false,
  maxAttendees: "",
  signupDeadlineLocal: "",
};

const AdminEventForm = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "ny";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    fetchEvent(id!)
      .then((event) => {
        if (cancelled) return;
        if (!event) {
          toast.error("Fant ikke arrangementet.");
          navigate("/admin/events", { replace: true });
          return;
        }
        setForm({
          title: event.title,
          slug: event.slug ?? "",
          startsAtLocal: isoToLocalInput(event.starts_at),
          durationMinutes: event.duration_minutes?.toString() ?? "",
          location: event.location ?? "",
          body: event.body ?? "",
          imagePath: event.image_path ?? "",
          signupHref: event.signup_href ?? "",
          isPublished: event.is_published,
          sortOrder: event.sort_order.toString(),
          enableSignup: event.enable_signup,
          maxAttendees: event.max_attendees?.toString() ?? "",
          signupDeadlineLocal: event.signup_deadline ? isoToLocalInput(event.signup_deadline) : "",
        });
      })
      .catch((err) => handleApiError(err, "Kunne ikke laste arrangementet."))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleImageUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const key = await uploadEventImage(file);
      update("imagePath", key);
      toast.success("Bilde lastet opp.");
    } catch (err) {
      handleApiError(err, "Kunne ikke laste opp bilde.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.startsAtLocal) {
      toast.error("Tittel og dato er påkrevd.");
      return;
    }
    setSaving(true);
    const startsAtIso = localInputToIso(form.startsAtLocal);
    // Preserve existing slug on edit; auto-generate on create.
    const slug = form.slug.trim() || generateEventSlug(form.title.trim(), startsAtIso);
    const input: EventInput = {
      title: form.title.trim(),
      slug,
      starts_at: startsAtIso,
      duration_minutes: form.durationMinutes ? Number(form.durationMinutes) : null,
      location: form.location.trim() || null,
      body: form.body.trim() || null,
      image_path: form.imagePath.trim() || null,
      signup_href: form.signupHref.trim() || null,
      is_published: form.isPublished,
      sort_order: Number(form.sortOrder) || 0,
      enable_signup: form.enableSignup,
      max_attendees: form.maxAttendees ? Number(form.maxAttendees) : null,
      signup_deadline: form.signupDeadlineLocal ? localInputToIso(form.signupDeadlineLocal) : null,
    };
    try {
      if (isNew) {
        await createEvent(input);
        toast.success("Arrangement opprettet.");
      } else {
        await updateEvent(id!, input);
        toast.success("Arrangement oppdatert.");
      }
      await queryClient.invalidateQueries({ queryKey: ["events"] });
      navigate("/admin/events");
    } catch (err) {
      handleApiError(err, "Kunne ikke lagre.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (isNew) return;
    if (!confirm("Slette dette arrangementet? Det kan ikke angres.")) return;
    setDeleting(true);
    try {
      await deleteEvent(id!);
      await queryClient.invalidateQueries({ queryKey: ["events"] });
      toast.success("Arrangement slettet.");
      navigate("/admin/events");
    } catch (err) {
      handleApiError(err, "Kunne ikke slette.");
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Laster …</p>;
  }

  const imagePreview = resolveEventImageUrl(form.imagePath || null);

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-3">
          <Link to="/admin/events">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tilbake
          </Link>
        </Button>
        <h1 className="mt-2 text-3xl font-semibold">
          {isNew ? "Nytt arrangement" : "Rediger arrangement"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="title">Tittel *</Label>
          <Input
            id="title"
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            required
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="startsAt">Dato og tid *</Label>
            <Input
              id="startsAt"
              type="datetime-local"
              value={form.startsAtLocal}
              onChange={(e) => update("startsAtLocal", e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="duration">Varighet (minutter)</Label>
            <Input
              id="duration"
              type="number"
              min={1}
              value={form.durationMinutes}
              onChange={(e) => update("durationMinutes", e.target.value)}
              placeholder="120"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="location">Sted</Label>
          <Input
            id="location"
            value={form.location}
            onChange={(e) => update("location", e.target.value)}
            placeholder="Håp Naturlab"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="body">Beskrivelse</Label>
          <Textarea
            id="body"
            rows={5}
            value={form.body}
            onChange={(e) => update("body", e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="image">Bilde</Label>
          <div className="flex items-start gap-4">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt=""
                className="h-24 w-32 rounded-md border object-cover"
              />
            ) : (
              <div className="flex h-24 w-32 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
                Ingen bilde
              </div>
            )}
            <div className="flex-1 space-y-2">
              <Input
                id="image"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
              />
              <p className="text-xs text-muted-foreground">
                {uploading ? "Laster opp …" : "JPG eller PNG. Anbefalt 1200×675."}
              </p>
              {form.imagePath && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => update("imagePath", "")}
                  className="-ml-2"
                >
                  Fjern bilde
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-3 rounded-md border p-4">
          <div className="flex items-center gap-3">
            <Switch
              id="enableSignup"
              checked={form.enableSignup}
              onCheckedChange={(checked) => update("enableSignup", checked)}
            />
            <div>
              <Label htmlFor="enableSignup" className="cursor-pointer">
                Aktiver påmelding
              </Label>
              <p className="text-xs text-muted-foreground">
                Når på får besøkende et påmeldingsskjema direkte i et popup.
              </p>
            </div>
          </div>
          {form.enableSignup && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="maxAttendees">Maks deltakere</Label>
                <Input
                  id="maxAttendees"
                  type="number"
                  min={1}
                  value={form.maxAttendees}
                  onChange={(e) => update("maxAttendees", e.target.value)}
                  placeholder="f.eks. 20"
                />
                <p className="text-xs text-muted-foreground">
                  La stå tom for ubegrenset.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="signupDeadline">Påmeldingsfrist</Label>
                <Input
                  id="signupDeadline"
                  type="datetime-local"
                  value={form.signupDeadlineLocal}
                  onChange={(e) => update("signupDeadlineLocal", e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Etter dette stenger påmeldingen automatisk.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="signupHref">Ekstern påmeldingslenke (kun hvis "Aktiver påmelding" er av)</Label>
          <Input
            id="signupHref"
            value={form.signupHref}
            onChange={(e) => update("signupHref", e.target.value)}
            placeholder="/kontakt"
            disabled={form.enableSignup}
          />
          <p className="text-xs text-muted-foreground">
            Brukes kun hvis du heller vil sende folk til kontakt-skjema eller en ekstern side.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sortOrder">Sortering</Label>
          <Input
            id="sortOrder"
            type="number"
            value={form.sortOrder}
            onChange={(e) => update("sortOrder", e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Lavere tall vises først. La stå på 0 hvis det ikke spiller noen rolle.
          </p>
        </div>

        <div className="flex items-center gap-3 rounded-md border p-4">
          <Switch
            id="isPublished"
            checked={form.isPublished}
            onCheckedChange={(checked) => update("isPublished", checked)}
          />
          <div>
            <Label htmlFor="isPublished" className="cursor-pointer">
              Publisert
            </Label>
            <p className="text-xs text-muted-foreground">
              Synlig på forsiden og /arrangementer når slått på.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between border-t pt-5">
          {!isNew ? (
            <Button
              type="button"
              variant="ghost"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleDelete}
              disabled={deleting}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {deleting ? "Sletter …" : "Slett"}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" asChild>
              <Link to="/admin/events">Avbryt</Link>
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Lagrer …" : isNew ? "Opprett" : "Lagre"}
            </Button>
          </div>
        </div>
      </form>

      {!isNew && id && (
        <div className="border-t pt-8">
          <AdminEventSignupsList eventId={id} />
        </div>
      )}
    </div>
  );
};

export default AdminEventForm;
